import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/view/components/ui/dialog'
import { Button } from '@/view/components/ui/button'
import { Input } from '@/view/components/ui/input'
import { FormProvider, useForm } from 'react-hook-form'
import { Label } from '@/view/components/ui/label'
import { Textarea } from '@/view/components/ui/textarea'
import { NotificationRecipient } from '@/app/entities/Notification'
import { BasicSelect } from '@/view/components/BasicSelect'
import { z } from 'zod'
import { useToast } from '@/view/components/ui/use-toast'
import { sendNotification } from '@/app/services/notificationService/sendNotification'
import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useMutation } from '@tanstack/react-query'
import { Spinner } from '@/view/components/ui/spinner'

const recipientOptions = [
  { value: NotificationRecipient.ALL, label: 'Todos os usuários' },
]

const schema = z.object({
  title: z.string().min(1, { message: 'Campo obrigatório' }),
  message: z.string().min(1, { message: 'Campo obrigatório' }),
  recipient: z.string().min(1, { message: 'Campo obrigatório' }),
})
interface NotificationModalProps {
  isOpen: boolean
  onOpenChange: (isOpean: boolean) => void
}

export const NotificationModal = ({
  isOpen,
  onOpenChange,
}: NotificationModalProps) => {
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      message: '',
      recipient: NotificationRecipient.ALL,
    },
  })

  const { toast } = useToast()

  const { register, reset, handleSubmit, formState } = form

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: async (formData: any) => {
      await sendNotification(formData)
      onOpenChange(false)
      reset()
    },
    onSuccess: () => {
      toast({
        variant: 'default',
        title: 'Notificação enviada!',
      })
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Ocorreu um erro. Tente novamente mais tarde',
      })
    },
  })

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="-mt-3 border-b pb-4">
          <DialogTitle className="text-xl font-semibold text-black01">
            Enviar notificação
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <div className="text-black04">
            <BasicSelect
              name="recipient"
              options={recipientOptions}
              placeholder="Quem receberá"
              label="Enviar para"
              error={formState.errors.recipient?.message as string}
            />
          </div>
          <Label
            htmlFor="title"
            className={clsx('inline-block font-medium text-black04', {
              'mt-4': !!formState.errors.recipient?.message,
            })}
          >
            Título
            <Input
              {...register('title')}
              name="title"
              type="text"
              className="mt-2 text-black"
            />
            {formState.errors.title?.message && (
              <div className="mt-1 text-sm font-light text-error">
                {formState.errors.title?.message as string}
              </div>
            )}
          </Label>

          <Label htmlFor="message" className="font-medium text-black04">
            Mensagem
            <Textarea
              {...register('message')}
              name="message"
              className="mt-2 text-black01"
              id="message"
            />
            {formState.errors.message?.message && (
              <div className="mt-1 text-sm font-light text-error">
                {formState.errors.message?.message as string}
              </div>
            )}
          </Label>
        </FormProvider>
        <DialogFooter>
          <Button
            variant="outline"
            className="mt-4 md:mt-0"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit((formData) => onSubmit(formData))}
            variant="default"
            className="bg-primary hover:bg-primary/90"
          >
            {isPending ? (
              <Spinner className="text-white" size="small" />
            ) : (
              'Enviar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
